/**
 * Journaled Candid authorization write-back payload.
 *
 * Coverage-service stores what it posted to Candid:
 * `{ authorizations: CandidJournalAuthorizationEntry[], count }`.
 * Each journal entry is one CPT with a nested `period` (see
 * `utility.providers.candid.models.authorization.CandidAuthorizationEntry`).
 *
 * {@link CandidAuthorizationWritebackPayload.parse} is the only conversion
 * the UI needs: unwrap the envelope, flatten each line, and re-join the
 * per-CPT fan-out into {@link CandidAuthorizationPayload} cards.
 *
 * @module candidAuthorizationWritebackPayload
 * @example
 * const cards = CandidAuthorizationWritebackPayload.parse(record.payload);
 */

/** Candid authorization unit: visit-based or unit-based. */
export type CandidAuthorizationUnit = "VISIT" | "UNIT";

/**
 * Validity window as stored on a journaled Candid authorization line.
 *
 * Dates are ISO calendar strings (`YYYY-MM-DD`).
 */
export interface CandidAuthorizationPeriod {
  /** Inclusive start date. */
  start: string;
  /** Inclusive end date; on or after `start`. */
  end: string;
}

/**
 * One authorization line as dumped into the write-back `payload` column.
 *
 * One CPT per entry. A multi-CPT approval is stored as sibling lines that
 * share authorization number, payer, and period.
 */
export interface CandidJournalAuthorizationEntry {
  /** Candid `claims_payer_id` (e.g. `'00590'`). */
  payer_id: string;
  /** Payer display name (e.g. `'AETNA'`). */
  payer_name: string;
  /** Payer-issued authorization number. */
  authorization_number: string;
  /** Single 5-character CPT/HCPCS code. */
  cpt_code: string;
  /** VISIT or UNIT. */
  units: CandidAuthorizationUnit;
  /** Approved quantity; usually 1. */
  quantity: number;
  /** Authorization validity window. */
  period: CandidAuthorizationPeriod;
  /** Unique ICD-10 diagnosis codes. */
  dx_codes: string[];
  /** Free-text note; null when omitted. */
  notes?: string | null;
  /** 10-digit billing provider NPI; null when omitted. */
  billing_provider_npi?: string | null;
}

/**
 * `payload` column for an authorization write-back row.
 *
 * `count` is the number of Candid lines appended; the UI ignores it.
 */
export interface CandidAuthorizationWritebackEnvelope {
  /** Per-CPT lines posted to Candid. */
  authorizations: CandidJournalAuthorizationEntry[];
  /** Number of lines in `authorizations`, when the journal includes it. */
  count?: number;
}

/**
 * Card-level authorization the UI renders.
 *
 * Same fields as the coverage-service request body (`AuthorizationInput`):
 * `cpt_codes[]` and flat `period_start` / `period_end`. Produced by
 * {@link CandidAuthorizationWritebackPayload.parse}.
 */
export interface CandidAuthorizationPayload {
  /** Candid `claims_payer_id` for this authorization. */
  payer_id: string;
  /** Payer display name for this authorization. */
  payer_name: string;
  /** Payer-issued authorization number. */
  authorization_number: string;
  /** One or more 5-character CPT/HCPCS codes. */
  cpt_codes: string[];
  /** Authorization unit: VISIT or UNIT. */
  units: CandidAuthorizationUnit;
  /** Approved quantity; usually 1. */
  quantity: number;
  /** Validity start date (`YYYY-MM-DD`). */
  period_start: string;
  /** Validity end date (`YYYY-MM-DD`); on or after `period_start`. */
  period_end: string;
  /** Unique ICD-10 diagnosis codes, e.g. `["N40.1"]`. */
  dx_codes: string[];
  /** Free-text note stored on the authorization. */
  notes: string;
  /** 10-digit ordering/billing provider NPI. */
  billing_provider_npi: string;
}

/**
 * Stored column value: Candid dump (`cpt_code` + `period`) or already-flat UI card.
 */
type StoredAuthorizationEntry =
  CandidJournalAuthorizationEntry | CandidAuthorizationPayload;

/**
 * Parses a write-back `payload` into card-level authorizations.
 *
 * Accepts the journal envelope, a bare array, a single journal line, or a
 * row already stored in {@link CandidAuthorizationPayload} shape.
 */
export class CandidAuthorizationWritebackPayload {
  /**
   * Unwraps, flattens, and merges a stored authorization `payload`.
   *
   * Sibling journal lines that share payer, authorization number, and
   * validity window are joined so one submitted auth is one card.
   *
   * @param payload - Write-back `payload` column value
   * @returns Card-level authorizations (possibly empty)
   */
  static parse(payload: unknown): CandidAuthorizationPayload[] {
    const merged = new Map<string, CandidAuthorizationPayload>();
    for (const entry of this.unwrapEntries(payload)) {
      const next = this.toCard(entry);
      const key = this.mergeKey(next);
      const existing = merged.get(key);
      if (!existing) {
        merged.set(key, next);
        continue;
      }
      for (const code of next.cpt_codes) {
        if (!existing.cpt_codes.includes(code)) {
          existing.cpt_codes.push(code);
        }
      }
    }
    return [...merged.values()];
  }

  /**
   * Pulls typed authorization objects out of the stored column.
   *
   * @param payload - Envelope, array, or single entry
   * @returns Journal lines or already-flat cards
   */
  private static unwrapEntries(payload: unknown): StoredAuthorizationEntry[] {
    const envelope = asRecord(payload);
    const source = envelope?.["authorizations"] ?? payload;
    return (Array.isArray(source) ? source : [source]).filter(
      isStoredAuthorization,
    );
  }

  /**
   * Maps a journal line onto a card, or returns an already-flat card as-is.
   *
   * @param entry - Journal line (`cpt_code` + `period`) or card payload
   * @returns Card-level authorization
   */
  private static toCard(
    entry: StoredAuthorizationEntry,
  ): CandidAuthorizationPayload {
    if (!isJournalAuthorization(entry)) {
      return entry;
    }
    return {
      payer_id: entry.payer_id,
      payer_name: entry.payer_name,
      authorization_number: entry.authorization_number,
      cpt_codes: [entry.cpt_code],
      units: entry.units,
      quantity: entry.quantity,
      period_start: entry.period.start,
      period_end: entry.period.end,
      dx_codes: entry.dx_codes,
      notes: entry.notes ?? "",
      billing_provider_npi: entry.billing_provider_npi ?? "",
    };
  }

  /**
   * Keys a card by payer, number, and validity window.
   *
   * @param payload - Card-level authorization
   * @returns Merge key
   */
  private static mergeKey(payload: CandidAuthorizationPayload): string {
    return [
      payload.payer_id,
      payload.authorization_number,
      payload.period_start,
      payload.period_end,
    ].join("|");
  }
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  !!value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Narrows JSON to a stored authorization line (journal or already-flat card).
 *
 * @param value - Unknown payload fragment
 * @returns Whether the value has an authorization number and a CPT field
 */
const isStoredAuthorization = (
  value: unknown,
): value is StoredAuthorizationEntry => {
  const entry = asRecord(value);
  return (
    !!entry &&
    "authorization_number" in entry &&
    ("cpt_code" in entry || "cpt_codes" in entry)
  );
};

const isJournalAuthorization = (
  entry: StoredAuthorizationEntry,
): entry is CandidJournalAuthorizationEntry => "period" in entry;
